import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "okccomets_flex",
  browserTitle: "Comets Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "OKC Comets",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-30349-baseball-sec",
    mapId: "blockmap2d",
    staticSeatMapImage: "", // Use empty string if 'UploadedToTicket'
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Add 2 more games for 20% off, 4+ games for 30% off including an OKC Comets hat, or 8 more games to include an on field batting practice experience!",
      },
      {
        id: 2,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your seats! Add 2 more games for 30% off and an OKC Comets hat, 6 more games to include an on field batting practice experience, or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 30,
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your seats and an OKC Comets hat! Add 4 more games for on field batting practice experience or checkout now.",
        customAditionalMessage: " for 30% off including an OKC Comets hat ",
      },
      {
        id: 4,
        minSelectedAmount: 10,
        updatedAccountGroup: 3,
        discount: 30,
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your seats and on field batting practice experience! Checkout now.",
        customAditionalMessage: "for an on field batting practice experience?",
      },
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL", // As specified

  // purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 16,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false, // Optional, defaulted to false
  shouldUseInternalLogin: false, // Optional, defaulted to false
  oktaClientId: "", // Not needed for Ticketmaster
  oktaIssuer: "", // Not needed for Ticketmaster
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "", // No value provided
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "460647028528234",

    trackMetaPixelOnCheckout: true,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl: "https://am.ticketmaster.com/okccomets/",

  // images
  images: {
    findGamesBannerImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/DBH%20Flex%20Assets/OKC%20Comets/1486x148%20OKC%20NEW%20OFFER.png",
    authBackgroundImage: "", // No value provided
    checkoutStatusPageImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/okc_comets_transparent.png",
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/okc_comets_transparent.png" +
      "?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor League Baseball/okc_comets_transparent.png",
    seatMap3dPreviewImage: "", // No value provided
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
  },

  colors: {
    primary: "#9eccea", // Light blue as specified
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "", // Can be a React node as well
    checkoutTermsAndCondition: <DefaultCheckoutTerms />, // Using standard SeasonShare language
    registrationTermsAndCondition: <DefaultRegistrationTerms />, // Using standard SeasonShare language
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Oklahoma City Comets tickets are confirmed! You will be notified when your tickets are available to be managed.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. They can use the gift card for all events at the OKC Comets.",
          },
          sendNowTrue: {
            description:
              "The recipient will be sent an email with the unique code for them to redeem their gift card. They can use the gift card for all events at the OKC Comets.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster account above.",
        },
        logout: {
          description:
            "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster.",
        },
        success: {
          description: "has been added to your account!",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
